import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Switch } from "antd";
import TableWithSkeleton from "../../components/global/table/TableWithSkeleton.jsx";
import ModalComponent from "../../components/global/modal/ModalComponent.jsx";
import LoadableButton from "../../components/buttons/LoadableButton.jsx";
import ConfirmationModal from "../../components/global/modal/ConfirmationModal.jsx";
import getEducationColumns from "../../columns/education.js";
import {
  GetAllEducationMaterialsApi,
  AddUpdateEducationMaterialApi,
  DeleteEducationMaterialApi,
} from "../../api/request/education.js";
import toast from "react-hot-toast";

const { TextArea } = Input;

export default function Education() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const getAllEducationMaterials = async () => {
    try {
      setIsLoading(true);
      const response = await GetAllEducationMaterialsApi();
      const responseData = response?.data;
      if (responseData?.success) {
        const mappedData = (responseData?.data || []).map((item) => ({ ...item, key: item?.id }));
        setData(mappedData);
      }
    } catch (error) {
      // Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const openAddModal = () => {
    setSelectedItem(null);
    form.setFieldsValue({ title: "", description: "", isActive: true });
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    form.setFieldsValue({
      title: item?.title,
      description: item?.description,
      isActive: item?.isActive ?? true,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      setIsSubmitLoading(true);
      const payload = {
        title: values?.title,
        description: values?.description,
        isActive: values?.isActive ?? true,
      };
      if (selectedItem?.id) {
        payload.id = selectedItem.id;
      }
      const response = await AddUpdateEducationMaterialApi(payload);
      if (response?.data?.success) {
        toast.success(
          response?.data?.message ||
            (selectedItem ? "Education material updated successfully." : "Education material added successfully.")
        );
        handleCloseModal();
        getAllEducationMaterials();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Something went wrong.");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete?.id) return;
    try {
      setIsDeleteLoading(true);
      const response = await DeleteEducationMaterialApi(itemToDelete.id);
      if (response?.data?.success) {
        toast.success(response?.data?.message || "Education material deleted successfully.");
        setDeleteModalOpen(false);
        setItemToDelete(null);
        getAllEducationMaterials();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Something went wrong.");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const openViewSections = (item) => {
    navigate(`/education/${item?.id}`);
  };

  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const columns = useMemo(
    () => getEducationColumns(openViewSections, openEditModal, openDeleteModal),
    []
  );

  useEffect(() => {
    getAllEducationMaterials();
  }, []);

  useEffect(() => {
    if (!isModalOpen) {
      setSelectedItem(null);
      form.resetFields();
    }
  }, [isModalOpen]);

  return (
    <div>
      <div className="flex justify-end items-center flex-wrap gap-5 mb-5">
        <button
          onClick={openAddModal}
          className="flex items-center bg-primary text-sm text-white font-semibold px-6 py-2 rounded-lg themeHover duration-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-5 h-5 mr-2"
          >
            <path
              fillRule="evenodd"
              d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
              clipRule="evenodd"
            />
          </svg>
          Add Education Material
        </button>
      </div>

      <div className="my-6">
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <TableWithSkeleton
            columns={columns}
            data={data}
            loading={isLoading}
            page={1}
            pageSize={10}
          />
        </div>
      </div>

      <ModalComponent
        isOpen={isModalOpen}
        setIsOpen={handleCloseModal}
        title={selectedItem ? "Edit Education Material" : "Add Education Material"}
        width={600}
      >
        <Form
          className="w-full"
          autoComplete="off"
          form={form}
          initialValues={{ title: "", description: "", isActive: true }}
          onFinish={handleSubmit}
        >
          <div className="grid sm:grid-cols-1 gap-5">
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Title</label>
              <Form.Item name="title" rules={[{ required: true, message: "Please enter a title." }]}>
                <Input placeholder="Title" className="mt-1 w-full py-2" />
              </Form.Item>
            </div>
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Description</label>
              <Form.Item name="description" rules={[{ required: true, message: "Please enter a description." }]}>
                <TextArea placeholder="Description..." className="mt-1 py-2" rows={4} />
              </Form.Item>
            </div>
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Status</label>
              <Form.Item name="isActive" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" defaultChecked />
              </Form.Item>
            </div>
          </div>
          <div className="flex justify-end mt-8">
            <LoadableButton
              className="bg-primary text-sm text-white font-semibold rounded-lg px-12 py-2 uppercase themeHover duration-500"
              disabled={isSubmitLoading}
              type="submit"
              lable={selectedItem ? "Update" : "Submit"}
              isLoading={isSubmitLoading}
              loadingLable={selectedItem ? "Updating..." : "Adding..."}
            />
          </div>
        </Form>
      </ModalComponent>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        setIsOpen={(open) => {
          setDeleteModalOpen(open);
          if (!open) setItemToDelete(null);
        }}
        message="Are you sure you want to delete this education material?"
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleteLoading}
        loadingLabel="Deleting..."
      />
    </div>
  );
}
